import { z } from "zod";
//#region src/kernel/kernel-rights.ts
/**
* The kernel rights, the tier type and the unattended set per right (kernel apps, WS-1a,
* decision 6, I-6, R-042; app mail, WS-1, D-13, D-14, D-18, D-20).
*
* A kernel right is a named group of privileged operations on a data plane, for example
* `identity.users`. The CP grants a kernel right to an app version, and the rights certificate
* carries the granted set (`rights-certificate.ts`). The DP kernel guard (WS-19) reads the right
* and the tier of each route from the route census (WS-2b), and reads the unattended rules below
* for a machine caller. The CP publish door (WS-8) reads the catalog for `appGrantable`.
*
* `KERNEL_RIGHTS` holds R1 to R12 of `research/p10-route-catalog.md` in that order, then R13
* `mail.send` and R14 `mail.external` of the app-mail design (`2026-10-09-app-mail`, D-18), then
* R15 `oidc.claims` of the app-oidc design (`2026-10-11-app-oidc`, D5, D6), then R16 `storage.global`
* of the blob-buckets design (`2026-10-13-blob-buckets`, D3, R-094 item 2). That order is canonical:
* one set of rights has one byte form in a signed statement or certificate.
* Each new right goes at the end, so every list that a signer wrote before it keeps its bytes.
*/
/**
* Every kernel right, R1 to R16, in canonical order. The length of this list is the count of
* kernel rights; no comment carries that count as a numeral.
*
* - R1 to R12: the admin rights of kernel apps (WS-1a).
* - R13 `mail.send`: an app sends mail through core-api `POST /internal/mail/app` to the active
*   members of its own organization (app mail, D-13, D-18).
* - R14 `mail.external`: an app sends that mail to an address outside its organization too
*   (D-14). The route asks for it in addition to `mail.send`, for each to, cc and bcc address
*   that is not an active member (D-23).
* - R15 `oidc.claims`: an app asks `POST /internal/v1/oidc-tokens` for an OIDC app token of its own
*   run with a `subject` or `claims` that the app chooses (app-oidc, D5, D15). A token with only the
*   platform claims needs no right; the route reads this one from the `krt` claim.
* - R16 `storage.global`: an app keeps a `scope: global` blob bucket, one bucket per app in the
*   control plane project that every org which consented at adoption shares (blob-buckets, D3,
*   R-094). The publish rule `blob-bucket-global-requires-right` of `blob-buckets.ts` refuses a
*   `global` bucket without it in `kernelRights`. A site admin approves it with one bucket location
*   (R-094 item 3). No route of a data plane reads it from the `krt` claim.
*/
const KERNEL_RIGHTS = [
	"identity.users",
	"identity.roles",
	"identity.providers",
	"machine.principals",
	"machine.keys",
	"authority.grants",
	"authority.groups",
	"credentials.mint",
	"credentials.llm",
	"tenant.settings",
	"audit.read",
	"identity.self",
	"mail.send",
	"mail.external",
	"oidc.claims",
	"storage.global"
];
/**
* The shape of a kernel right name: two lower-case words with a full stop between them. The JSON
* Schema of `app.json` bounds `kernelRights` items to this pattern with no enum, so an operator
* program that predates a newer right still parses a stored manifest.
*/
const KERNEL_RIGHT_PATTERN = /^[a-z]+\.[a-z]+$/;
/** The zod enum of {@link KERNEL_RIGHTS}. It refuses every other string. */
const kernelRightSchema = z.enum(KERNEL_RIGHTS);
const KERNEL_RIGHT_INDEX = new Map(KERNEL_RIGHTS.map((right, index) => [right, index]));
/** The canonical position of `right`, or -1 for a value that is not a kernel right. */
function kernelRightIndex(right) {
	return typeof right === "string" ? KERNEL_RIGHT_INDEX.get(right) ?? -1 : -1;
}
/**
* A list of kernel rights in its one byte form. It admits 0 to `KERNEL_RIGHTS.length` entries,
* and a longer list gets a `too_big` issue at the list. It refuses an unknown
* name, a duplicate and a list out of canonical order, so a signer and a verifier hash the same
* bytes. Call {@link sortKernelRights} first to put a set in canonical order.
*/
const kernelRightListSchema = z.array(kernelRightSchema).max(KERNEL_RIGHTS.length).superRefine((rights, ctx) => {
	for (let i = 1; i < rights.length; i += 1) if (kernelRightIndex(rights[i]) <= kernelRightIndex(rights[i - 1])) {
		ctx.addIssue({
			code: "custom",
			path: [i],
			message: "kernel rights must be unique and in canonical order (KERNEL_RIGHTS)"
		});
		return;
	}
});
/** True when `value` is one of {@link KERNEL_RIGHTS}. Never throws. Pure. */
function isKernelRight(value) {
	return kernelRightIndex(value) >= 0;
}
/**
* A new list with the members of `rights` in canonical order and no duplicate. The input stays
* as it is. A value that is not a kernel right at run time stays in the list, at the front, so a
* later parse refuses it and nothing drops it in silence. Pure.
*/
function sortKernelRights(rights) {
	return [...new Set(rights)].sort((a, b) => kernelRightIndex(a) - kernelRightIndex(b));
}
/**
* True when `granted` holds every right in `asked`. An empty `asked` list is a subset of any list.
* Order and duplicates do not matter. Pure.
*/
function isKernelRightSubset(asked, granted) {
	const held = new Set(granted);
	return asked.every((right) => held.has(right));
}
/**
* The three tiers of a kernel route.
*
* - `0`: the caller needs a kernel session.
* - `1`: the caller needs a step-up bound to the one operation.
* - `2`: the caller needs a step-up of the same kernel session within the step-up window (WS-1b).
*/
const KERNEL_TIERS = [
	0,
	1,
	2
];
/** The label, the consent sentence and `appGrantable` of each kernel right. */
const KERNEL_RIGHT_CATALOG = {
	"identity.users": {
		right: "identity.users",
		label: "Users",
		description: "Create, change, deactivate and remove the people of the organization, their invitations and their sessions.",
		appGrantable: true
	},
	"identity.roles": {
		right: "identity.roles",
		label: "Roles",
		description: "Define roles and give roles and app permissions to people and groups.",
		appGrantable: true
	},
	"identity.providers": {
		right: "identity.providers",
		label: "Login providers",
		description: "Change who can log in: identity providers, recovery codes, SCIM tokens and OAuth clients.",
		appGrantable: true
	},
	"machine.principals": {
		right: "machine.principals",
		label: "Machine principals",
		description: "Enrol, change and revoke machine principals and the outside issuers they trust.",
		appGrantable: true
	},
	"machine.keys": {
		right: "machine.keys",
		label: "Machine keys",
		description: "Register and revoke the keys and enrol tokens of machine principals.",
		appGrantable: true
	},
	"authority.grants": {
		right: "authority.grants",
		label: "Resource grants",
		description: "Give and revoke per-object grants and the platform permissions of apps.",
		appGrantable: true
	},
	"authority.groups": {
		right: "authority.groups",
		label: "Groups",
		description: "Create, change and remove groups, their members and their child groups.",
		appGrantable: true
	},
	"credentials.mint": {
		right: "credentials.mint",
		label: "Credential mint",
		description: "Mint service tokens, NATS identity credentials and agent capabilities.",
		appGrantable: true
	},
	"credentials.llm": {
		right: "credentials.llm",
		label: "LLM credentials",
		description: "Store, test and remove the language model provider keys of the organization.",
		appGrantable: true
	},
	"tenant.settings": {
		right: "tenant.settings",
		label: "Organization settings",
		description: "Change the organization settings, its domains, its release choices and its channels.",
		appGrantable: true
	},
	"audit.read": {
		right: "audit.read",
		label: "Audit log",
		description: "Read the audit log and the session list of the organization.",
		appGrantable: true
	},
	"identity.self": {
		right: "identity.self",
		label: "Own login factors",
		description: "Add and remove the passkeys and the password of the person who is signed in.",
		appGrantable: false
	},
	"mail.send": {
		right: "mail.send",
		label: "Mail to members",
		description: "Send mail from the app, with any content and attachments, to the active members of the organization.",
		appGrantable: true
	},
	"mail.external": {
		right: "mail.external",
		label: "Mail to any address",
		description: "Send mail from the app to addresses outside the organization, together with the Mail to members right.",
		appGrantable: true
	},
	"oidc.claims": {
		right: "oidc.claims",
		label: "OIDC token claims",
		description: "Sign OIDC tokens for the runs of the app with a subject and claims that the app chooses.",
		appGrantable: true
	},
	"storage.global": {
		right: "storage.global",
		label: "Shared storage",
		description: "Keep app data in one bucket in the control plane project that every organization which accepted the app shares.",
		appGrantable: true
	}
};
/**
* The conditions of an unattended rule. WS-19 evaluates each one.
*
* - `always`: no further condition. It stands alone in a rule.
* - `same-app-target`: the target principal belongs to the calling app.
* - `within-delegatable-roles`: every role in the write is in the app's delegatable set.
* - `app-declared-type`: the grant type is one the app declares (`resourceTypes`).
* - `app-owned-group`: the calling app owns the group.
*/
const KERNEL_UNATTENDED_CONDITIONS = [
	"always",
	"same-app-target",
	"within-delegatable-roles",
	"app-declared-type",
	"app-owned-group"
];
const GROUP = "/v1/admin/groups/:id";
const APP_OWNED_GROUP = ["app-owned-group"];
/**
* The unattended rules per right (decision 6, narrowed by I-6). The rows come from the route
* decorators of core-api. A right with no row has an empty list, so every machine call under it
* needs a step-up request. `identity.roles` has no row (WS-1 open question 6, R-075).
*
* `mail.send` has one row: `POST /internal/mail/app` with `['always']` (app mail, D-20, R-002).
* It landed in the same change as the marked core-api route (WS-5, stage S4), because every row
* must name a served route with a mark of its right (I-6). So a machine caller that holds
* `mail.send` in its `krt` claim sends mail with no step-up request. `mail.external` has no row:
* no route carries it as its mark. The mail route reads it from the `krt` claim (D-19).
*
* `oidc.claims` has no row either (app-oidc). No route carries it as its mark:
* `POST /internal/v1/oidc-tokens` reads it from the `krt` claim only when the request asserts a
* `subject` or `claims`.
*
* `storage.global` has no row (blob-buckets, R-094). No data plane route carries it as its mark. The
* control plane reads it: at publish, at bucket allocation, at the approve of a kernel-right request
* that names it (the bucket location of R-094 item 3), and in the cp-admin-ui decision panel.
*/
const KERNEL_UNATTENDED_RULES = {
	"identity.users": [],
	"identity.roles": [],
	"identity.providers": [],
	"machine.principals": [
		{
			operation: "POST /internal/v1/machine-principals/enroll",
			conditions: ["always"]
		},
		{
			operation: "PATCH /internal/v1/machine-principals/:id",
			conditions: ["same-app-target", "within-delegatable-roles"]
		},
		{
			operation: "POST /internal/v1/machine-principals/:id/revoke",
			conditions: ["same-app-target"]
		}
	],
	"machine.keys": [],
	"authority.grants": [{
		operation: "POST /v1/admin/resource-grants",
		conditions: ["app-declared-type"]
	}, {
		operation: "DELETE /v1/admin/resource-grants/:id",
		conditions: ["app-declared-type"]
	}],
	"authority.groups": [
		{
			operation: "POST /v1/admin/groups",
			conditions: ["always"]
		},
		{
			operation: `PATCH ${GROUP}`,
			conditions: APP_OWNED_GROUP
		},
		{
			operation: `DELETE ${GROUP}`,
			conditions: APP_OWNED_GROUP
		},
		{
			operation: `POST ${GROUP}/members`,
			conditions: APP_OWNED_GROUP
		},
		{
			operation: `DELETE ${GROUP}/members/:userId`,
			conditions: APP_OWNED_GROUP
		},
		{
			operation: `POST ${GROUP}/child-groups`,
			conditions: APP_OWNED_GROUP
		},
		{
			operation: `DELETE ${GROUP}/child-groups/:childId`,
			conditions: APP_OWNED_GROUP
		},
		{
			operation: `POST ${GROUP}/child-groups/:childId/move`,
			conditions: APP_OWNED_GROUP
		}
	],
	"credentials.mint": [{
		operation: "POST /internal/v1/nats/identity-creds",
		conditions: ["always"]
	}, {
		operation: "POST /internal/v1/agent-capabilities",
		conditions: ["always"]
	}],
	"credentials.llm": [],
	"tenant.settings": [],
	"audit.read": [],
	"identity.self": [],
	"mail.send": [{
		operation: "POST /internal/mail/app",
		conditions: ["always"]
	}],
	"mail.external": [],
	"oidc.claims": [],
	"storage.global": []
};
/**
* The unattended rule of `right` for the exact key `operation`, or `null`. `null` means the
* machine caller needs a step-up request. The lookup does not match a key under another right.
* Pure.
*/
function kernelUnattendedRule(right, operation) {
	return KERNEL_UNATTENDED_RULES[right].find((rule) => rule.operation === operation) ?? null;
}
//#endregion
export { KERNEL_RIGHTS, KERNEL_RIGHT_CATALOG, KERNEL_RIGHT_PATTERN, KERNEL_TIERS, KERNEL_UNATTENDED_CONDITIONS, KERNEL_UNATTENDED_RULES, isKernelRight, isKernelRightSubset, kernelRightListSchema, kernelRightSchema, kernelUnattendedRule, sortKernelRights };

//# sourceMappingURL=kernel-rights.js.map